import { createSlice } from '@reduxjs/toolkit'
import { getMission, getMissionData } from './action'
import type { Mission, MissionData, MissionProgress } from './types'

export interface MissionState {
  currentMission: Mission | null
  missionsCache: Record<number, Mission>
  missionProgress: MissionProgress | null
  currentMissionData: MissionData | null
  missionsDataCache: Record<number, MissionData>
  /** Global objective-complete toast; shown in App so it's visible on any page. */
  showObjectiveCompleteNotification: boolean
  objectiveCompleteDescription: string | null
  /** Legacy payload when showing notification (objective complete API runs in MissionPack before toast). */
  pendingObjectiveComplete: { day: number; questId: number; objectiveId: number } | null
  /** Global quest-complete toast; shown in App so it's visible on any page. */
  showQuestCompleteNotification: boolean
  questCompleteDescription: string | null
  /** Set with toast; quest complete API runs in MissionPack before toast. */
  pendingQuestComplete: { day: number; questId: number } | null
  /** Global mission-complete toast; shown after claiming last quest of a mission. */
  showMissionCompleteNotification: boolean
  /** Set when showing mission-complete toast; used to POST /mission/mission/complete when user dismisses. */
  pendingMissionComplete: number | null
  /** After successful mission complete + getMission; MissionPack opens next mission video or panel. */
  openNextMissionIntroVideoPending: boolean
  /** True while the mission quest panel or intro video is open (used by FTUE stage gating). */
  missionPanelOpen: boolean
  walletAddress: string
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  error: string | null
  dataStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  dataError: string | null
}

const initialState: MissionState = {
  currentMission: null,
  missionsCache: {},
  missionProgress: null,
  currentMissionData: null,
  missionsDataCache: {},
  showObjectiveCompleteNotification: false,
  objectiveCompleteDescription: null,
  pendingObjectiveComplete: null,
  showQuestCompleteNotification: false,
  questCompleteDescription: null,
  pendingQuestComplete: null,
  showMissionCompleteNotification: false,
  pendingMissionComplete: null,
  openNextMissionIntroVideoPending: false,
  missionPanelOpen: false,
  walletAddress: '',
  status: 'idle',
  error: null,
  dataStatus: 'idle',
  dataError: null
}

const missionSlice = createSlice({
  name: 'mission',
  initialState,
  reducers: {
    clearMissionError: (state) => {
      state.error = null
    },
    clearMissionDataError: (state) => {
      state.dataError = null
    },
    setShowObjectiveCompleteNotification: (
      state,
      action: {
        payload:
          | boolean
          | {
              show: boolean
              description?: string | null
              day?: number
              questId?: number
              objectiveId?: number
            }
      }
    ) => {
      const payload = action.payload
      if (typeof payload === 'boolean') {
        state.showObjectiveCompleteNotification = payload
        state.objectiveCompleteDescription = null
        state.pendingObjectiveComplete = null
      } else {
        state.showObjectiveCompleteNotification = payload.show
        state.objectiveCompleteDescription = payload.show ? (payload.description ?? null) : null
        state.pendingObjectiveComplete =
          payload.show &&
          payload.day != null &&
          payload.questId != null &&
          payload.objectiveId != null
            ? { day: payload.day, questId: payload.questId, objectiveId: payload.objectiveId }
            : null
      }
    },
    setShowQuestCompleteNotification: (
      state,
      action: {
        payload:
          | boolean
          | {
              show: boolean
              description?: string | null
              day?: number
              questId?: number
            }
      }
    ) => {
      const payload = action.payload
      if (typeof payload === 'boolean') {
        state.showQuestCompleteNotification = payload
        state.questCompleteDescription = null
        state.pendingQuestComplete = null
      } else {
        state.showQuestCompleteNotification = payload.show
        state.questCompleteDescription = payload.show ? (payload.description ?? null) : null
        state.pendingQuestComplete =
          payload.show && payload.day != null && payload.questId != null
            ? { day: payload.day, questId: payload.questId }
            : null
      }
    },
    setShowMissionCompleteNotification: (
      state,
      action: { payload: boolean | { show: boolean; missionDay?: number } }
    ) => {
      const payload = action.payload
      if (typeof payload === 'boolean') {
        state.showMissionCompleteNotification = payload
        if (!payload) state.pendingMissionComplete = null
      } else {
        state.showMissionCompleteNotification = payload.show
        state.pendingMissionComplete =
          payload.show && payload.missionDay != null ? payload.missionDay : null
      }
    },
    setOpenNextMissionIntroVideoPending: (state, action: { payload: boolean }) => {
      state.openNextMissionIntroVideoPending = action.payload
    },
    setMissionPanelOpen: (state, action: { payload: boolean }) => {
      state.missionPanelOpen = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMission.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getMission.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.error = null
        state.walletAddress = action.payload.walletAddress
        if (action.payload.missionProgress != null) {
          state.missionProgress = action.payload.missionProgress
        }
        const mission = action.payload.mission
        if (mission) {
          state.missionsCache[mission.missionDay] = mission
          if (action.meta.arg.day === undefined || action.meta.arg.day === null) {
            state.currentMission = mission
          }
        } else {
          if (action.meta.arg.day === undefined || action.meta.arg.day === null) {
            state.currentMission = null
          }
        }
      })
      .addCase(getMission.rejected, (state, action) => {
        state.status = 'failed'
        state.error = (action.payload as string | undefined) ?? 'Failed to fetch mission'
      })
      .addCase(getMissionData.pending, (state) => {
        state.dataStatus = 'loading'
        state.dataError = null
      })
      .addCase(getMissionData.fulfilled, (state, action) => {
        state.dataStatus = 'succeeded'
        state.dataError = null
        if (action.payload.missionData) {
          const missionData = action.payload.missionData
          state.missionsDataCache[missionData.day] = missionData
          if (state.missionProgress?.missionDay === missionData.day) {
            state.currentMissionData = missionData
          }
        }
        if (action.payload.missionDataList) {
          const list = Array.isArray(action.payload.missionDataList)
            ? action.payload.missionDataList
            : [action.payload.missionDataList]
          list.forEach((missionData: MissionData) => {
            state.missionsDataCache[missionData.day] = missionData
          })
          if (state.missionProgress && list.length > 0) {
            const current = list.find((m: MissionData) => m.day === state.missionProgress?.missionDay)
            state.currentMissionData = current ?? state.currentMissionData
          }
        }
      })
      .addCase(getMissionData.rejected, (state, action) => {
        state.dataStatus = 'failed'
        state.dataError = (action.payload as string | undefined) ?? 'Failed to fetch mission data'
      })
  }
})

export const {
  clearMissionError,
  clearMissionDataError,
  setShowObjectiveCompleteNotification,
  setShowQuestCompleteNotification,
  setShowMissionCompleteNotification,
  setOpenNextMissionIntroVideoPending,
  setMissionPanelOpen
} = missionSlice.actions
export default missionSlice.reducer
